import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../AddTeacher/AddTeacher.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = "https://aadyaacademy.org/api";

function AddTeacher({ handleCancel, fetchData, teacher }) {
  const [formData, setFormData] = useState({
    name: "",
    department: "",
    email: "",
    phone: "",
    gender: "",
    degree: "",
    city: "",
    joinDate: "",
    classteacher: [],
    subject: [],
    section: [],
  });

  const [errors, setErrors] = useState({
    name: "",
    gender: "",
    phone: "",
    email: "",
  });

  const [departments, setDepartments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  const [isSubjectDropdownOpen, setIsSubjectDropdownOpen] = useState(false);
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);

  const classDropdownRef = useRef(null);
  const subjectDropdownRef = useRef(null);
  const sectionDropdownRef = useRef(null);

  useEffect(() => {
    if (teacher) {
      console.log("Teacher prop in AddTeacher:", teacher); // Debug log
      setFormData({
        name: teacher.name || "",
        department: teacher.department || "",
        email: teacher.email || "",
        phone: teacher.phone || "",
        gender: teacher.gender || "",
        degree: teacher.degree || "",
        city: teacher.city || "",
        joinDate: teacher.joinDate || "",
        // Handle classteacher as array, no split needed
        classteacher: Array.isArray(teacher.classteacher) ? teacher.classteacher : (teacher.classteacher ? teacher.classteacher.split(",") : []),
        // Handle subject as array, no split needed
        subject: Array.isArray(teacher.subject) ? teacher.subject : (teacher.subject ? teacher.subject.split(",") : []),
        // Handle section as array, no split needed
        section: Array.isArray(teacher.section) ? teacher.section : (teacher.section ? teacher.section.split(",") : []),
      });
      setErrors({ name: "", gender: "", phone: "", email: "" });
    }
  }, [teacher]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API_URL}/departments`);
        setDepartments(response.data || []);
      } catch (error) {
        console.error("Error fetching department data:", error);
        toast.error("Failed to fetch departments.");
      }
    };

    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/subjects`);
        setSubjects(response.data || []);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        toast.error("Failed to fetch subjects.");
      }
    };

    const fetchSections = async () => {
      try {
        const response = await axios.get(`${API_URL}/sections`);
        setSections(response.data?.sections || []);
      } catch (error) {
        console.error("Error fetching sections:", error);
        toast.error("Failed to fetch sections.");
      }
    };

    fetchDepartments();
    fetchSubjects();
    fetchSections();
  }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(`${API_URL}/classes`);
        setClasses(response.data?.classes || []);
      } catch (error) {
        console.error("Error fetching classes:", error);
        toast.error("Failed to fetch classes.");
      }
    };

    fetchClasses();
  }, []);

  const validateEmail = (email) => {
    if (!email) return true;
    if (!email.includes("@")) return true;
    return /^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (["name", "degree", "city"].includes(name)) {
      newValue = value.replace(/\d/g, "");
    } else if (name === "phone") {
      if (!/^\d{0,10}$/.test(value)) {
        return;
      }
    }

    setFormData({ ...formData, [name]: newValue });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: !newValue && ["name", "gender", "phone"].includes(name)
        ? `${name.charAt(0).toUpperCase() + name.slice(1)} is required`
        : name === "email" && newValue && !validateEmail(newValue)
        ? "Email must end with @gmail.com or be a valid identifier"
        : "",
    }));
  };

  const handleClassToggle = (className) => {
    setFormData((prevFormData) => {
      const currentClasses = prevFormData.classteacher;
      if (currentClasses.includes(className)) {
        return {
          ...prevFormData,
          classteacher: currentClasses.filter((cls) => cls !== className),
        };
      } else {
        return {
          ...prevFormData,
          classteacher: [...currentClasses, className],
        };
      }
    });
  };

  const handleSubjectToggle = (subjectName) => {
    setFormData((prevFormData) => {
      const currentSubjects = prevFormData.subject;
      if (currentSubjects.includes(subjectName)) {
        return {
          ...prevFormData,
          subject: currentSubjects.filter((sub) => sub !== subjectName),
        };
      } else {
        return {
          ...prevFormData,
          subject: [...currentSubjects, subjectName],
        };
      }
    });
  };

  const handleSectionToggle = (sectionName) => {
    setFormData((prevFormData) => {
      const currentSections = prevFormData.section;
      if (currentSections.includes(sectionName)) {
        return {
          ...prevFormData,
          section: currentSections.filter((sec) => sec !== sectionName),
        };
      } else {
        return {
          ...prevFormData,
          section: [...currentSections, sectionName],
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.gender || !formData.phone) {
      setErrors({
        name: !formData.name ? "Name is required" : "",
        gender: !formData.gender ? "Gender is required" : "",
        phone: !formData.phone ? "Phone is required" : "",
        email: formData.email && !validateEmail(formData.email)
          ? "Email must end with @gmail.com or be a valid identifier"
          : "",
      });
      toast.error("Please fill all required fields.");
      return;
    }

    if (formData.email && !validateEmail(formData.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Email must end with @gmail.com or be a valid identifier",
      }));
      toast.error("Invalid email format.");
      return;
    }

    // Use only the first selected section for class_teacher_for
    const firstSection = formData.section.length > 0 ? formData.section[0] : null;
    const classTeacherFor = formData.classteacher.map((cls) => ({
      class: cls,
      section: firstSection,
    }));

    // Generate unique combinations for subject_teacher_for
    const subjectTeacherFor = [];
    formData.classteacher.forEach((cls) => {
      formData.section.forEach((sec) => {
        formData.subject.forEach((sub) => {
          if (!subjectTeacherFor.some(item => item.class === cls && item.section === sec && item.subject === sub)) {
            subjectTeacherFor.push({ class: cls, section: sec, subject: sub });
          }
        });
      });
    });

    // Prepare data payload
    const data = {
      name: formData.name,
      gender: formData.gender,
      phone: formData.phone,
      ...(formData.department && { department: formData.department }),
      ...(formData.email && { email: formData.email }),
      ...(formData.degree && { degree: formData.degree }),
      ...(formData.city && { city: formData.city }),
      ...(formData.joinDate && { joinDate: formData.joinDate }),
      ...(formData.classteacher.length > 0 && { classteacher: formData.classteacher.join(",") }),
      ...(formData.subject.length > 0 && { subject: formData.subject.join(",") }),
      ...(formData.section.length > 0 && { section: formData.section.join(",") }),
      ...(classTeacherFor.length > 0 && { class_teacher_for: classTeacherFor }),
      ...(subjectTeacherFor.length > 0 && { subject_teacher_for: subjectTeacherFor }),
    };

    try {
      if (teacher) {
        await axios.put(`${API_URL}/teachers/${teacher.id}`, data, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Teacher updated successfully.");
      } else {
        await axios.post(`${API_URL}/teachers`, data, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Teacher added successfully.");
      }
      fetchData();
      handleCancel();
    } catch (error) {
      console.error("Error adding/updating teacher:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to save teacher. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleClear = () => {
    setFormData({
      name: "",
      department: "",
      email: "",
      phone: "",
      gender: "",
      degree: "",
      city: "",
      joinDate: "",
      classteacher: [],
      subject: [],
      section: [],
    });
    setErrors({ name: "", gender: "", phone: "", email: "" });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classDropdownRef.current && !classDropdownRef.current.contains(event.target)) {
        setIsClassDropdownOpen(false);
      }
      if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(event.target)) {
        setIsSubjectDropdownOpen(false);
      }
      if (sectionDropdownRef.current && !sectionDropdownRef.current.contains(event.target)) {
        setIsSectionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="add-teacher-form">
      <div className="teacher-c">
        <h2>{teacher ? "Edit Teacher" : "Add Teacher"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="n-d-e">
            <div className="tea-name">
              <h4 className="required-field">Name *</h4>
              <input
                type="text"
                className={`teachername ${errors.name ? "input-error" : ""}`}
                name="name"
                placeholder="Enter name"
                value={formData.name}
                onChange={handleChange}
                aria-required="true"
                aria-describedby="name-error"
              />
              {errors.name && (
                <span id="name-error" className="error-message">
                  {errors.name}
                </span>
              )}
            </div>
            <div className="tea-department">
              <h4>Department</h4>
              <select
                className="teachername"
                name="department"
                value={formData.department}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {departments.length > 0 ? (
                  departments.map((department) => (
                    <option key={department.id} value={department.names}>
                      {department.names}
                    </option>
                  ))
                ) : (
                  <option value="">No departments available</option>
                )}
              </select>
            </div>
            <div className="tea-email">
              <h4>Email</h4>
              <input
                className={`teachername ${errors.email ? "input-error" : ""}`}
                type="text"
                name="email"
                placeholder="Enter email (e.g., user@gmail.com or identifier)"
                value={formData.email}
                onChange={handleChange}
                aria-describedby="email-error"
              />
              {errors.email && (
                <span id="email-error" className="error-message">
                  {errors.email}
                </span>
              )}
            </div>
          </div>
          <div className="p-g-d">
            <div className="tea-phone">
              <h4 className="required-field">Phone Number *</h4>
              <input
                className={`teachername ${errors.phone ? "input-error" : ""}`}
                type="text"
                name="phone"
                placeholder="Enter 10-digit phone"
                value={formData.phone}
                onChange={handleChange}
                aria-required="true"
                aria-describedby="phone-error"
              />
              {errors.phone && (
                <span id="phone-error" className="error-message">
                  {errors.phone}
                </span>
              )}
            </div>
            <div className="tea-gender">
              <h4 className="required-field">Gender *</h4>
              <select
                className={`teachername ${errors.gender ? "input-error" : ""}`}
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                aria-required="true"
                aria-describedby="gender-error"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              {errors.gender && (
                <span id="gender-error" className="error-message">
                  {errors.gender}
                </span>
              )}
            </div>
            <div className="tea-degree">
              <h4>Degree</h4>
              <input
                className="teachername"
                type="text"
                name="degree"
                placeholder="Enter degree"
                value={formData.degree}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="c-d-s">
            <div className="tea-city">
              <h4>City</h4>
              <input
                className="teachername"
                type="text"
                name="city"
                placeholder="Enter city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div className="tea-dateofjoin">
              <h4>Date of Join</h4>
              <input
                className="teachername"
                type="date"
                name="joinDate"
                value={formData.joinDate}
                onChange={handleChange}
              />
            </div>
            <div className="tea-classteacher" ref={classDropdownRef}>
              <h4>Class Teacher</h4>
              <div className="custom-select">
                <div
                  className="teachername custom-select-header"
                  onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                >
                  {formData.classteacher.length > 0
                    ? formData.classteacher.join(", ")
                    : "Select Classes"}
                </div>
                {isClassDropdownOpen && (
                  <ul className="custom-select-options">
                    {["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "Mont. Core"].map((cls) => (
                      <li key={cls} className="custom-select-option">
                        <input
                          type="checkbox"
                          id={`class-${cls}`}
                          checked={formData.classteacher.includes(cls)}
                          onChange={() => handleClassToggle(cls)}
                        />
                        <label htmlFor={`class-${cls}`}>
                          <span className="checkmark-icon"></span> {cls}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="tea-subject" ref={subjectDropdownRef}>
              <h4>Subject</h4>
              <div className="custom-select">
                <div
                  className="teachername custom-select-header"
                  onClick={() => setIsSubjectDropdownOpen(!isSubjectDropdownOpen)}
                >
                  {formData.subject.length > 0
                    ? formData.subject.join(", ")
                    : "Select Subjects"}
                </div>
                {isSubjectDropdownOpen && (
                  <ul className="custom-select-options">
                    {subjects.length > 0 ? (
                      subjects.map((subject) => (
                        <li key={subject.id} className="custom-select-option">
                          <input
                            type="checkbox"
                            id={`subject-${subject.id}`}
                            checked={formData.subject.includes(subject.names)}
                            onChange={() => handleSubjectToggle(subject.names)}
                          />
                          <label htmlFor={`subject-${subject.id}`}>
                            <span className="checkmark-icon"></span> {subject.names}
                          </label>
                        </li>
                      ))
                    ) : (
                      <li className="custom-select-option">No subjects available</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
            <div className="tea-section" ref={sectionDropdownRef}>
              <h4>Section</h4>
              <div className="custom-select">
                <div
                  className="teachername custom-select-header"
                  onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
                >
                  {formData.section.length > 0
                    ? formData.section.join(", ")
                    : "Select Sections"}
                </div>
                {isSectionDropdownOpen && (
                  <ul className="custom-select-options">
                    {sections.length > 0 ? (
                      sections.map((section) => (
                        <li key={section.id} className="custom-select-option">
                          <input
                            type="checkbox"
                            id={`section-${section.id}`}
                            checked={formData.section.includes(section.name)}
                            onChange={() => handleSectionToggle(section.name)}
                          />
                          <label htmlFor={`section-${section.id}`}>
                            <span className="checkmark-icon"></span> {section.name}
                          </label>
                        </li>
                      ))
                    ) : (
                      <li className="custom-select-option">No sections available</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
          <div className="form-buttons">
            <button className="form-buttons-submit" type="submit">
              Submit
            </button>
            <button className="form-buttons-clr" type="button" onClick={handleClear}>
              Clear
            </button>
            <button className="form-buttons-cancel" type="button" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
}

export default AddTeacher;